import React, { useState, useEffect } from "react";
import { CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useDashboardData } from "@/services/dashboardDataService";
import { useDashboardWidgetContext } from "@/contexts/DashboardWidgetContext";
import "./widgets.css";
import { cleanDateAndTimeObject } from "@shared/renderDate";

const WIDGET_ID = 'on-time-performance';

interface OnTimePerformanceData {
  metrics: {
    totalTasks: number;
    completedTasks: number;
    onTimeOrEarly: {
      count: number;
      percentage: number;
    };
    late: {
      count: number;
      percentage: number;
    };
    status: 'good' | 'warning' | 'critical' | 'unknown';
  };
  lastUpdated?: {
    toDate?: () => Date;
  };
  error?: boolean;
  errorMessage?: string;
}

const OnTimePerformanceWidget: React.FC = () => {
  const { data, isLoading, error, refetch } = useDashboardData('on-time-performance');
  const { reportWidgetStatus } = useDashboardWidgetContext();
  const metrics = data?.metrics as OnTimePerformanceData['metrics'] | undefined;
  const [showErrorDialog, setShowErrorDialog] = useState(false);

  // Report widget status to dashboard context
  useEffect(() => {
    if (isLoading) {
      reportWidgetStatus(WIDGET_ID, 'loading');
    } else if (error && !metrics) {
      reportWidgetStatus(WIDGET_ID, 'error');
    } else if (!metrics) {
      reportWidgetStatus(WIDGET_ID, 'no-data');
    } else {
      reportWidgetStatus(WIDGET_ID, 'success');
    }
  }, [isLoading, error, metrics, reportWidgetStatus]);

  // Get status display based on metrics
  const getStatus = () => {
    if (!metrics) {
      return {
        icon: "ℹ️",
        color: "text-blue-500",
        text: "No data available",
      };
    }

    const { status, onTimeOrEarly, late } = metrics;

    if (status === 'critical') {
      return {
        icon: "❌",
        color: "text-red-500",
        text: `${late.percentage.toFixed(1)}% of completed tasks are late.`,
      };
    } else if (status === 'good') {
      return {
        icon: "✅",
        color: "text-green-600",
        text: `${onTimeOrEarly.percentage.toFixed(1)}% of completed tasks are on time or early.`,
      };
    } else {
      return {
        icon: "⚠️",
        color: "text-orange-500",
        text: `${late.percentage.toFixed(1)}% of completed tasks are late.`,
      };
    }
  };

  const status = getStatus();
  const completionRate = metrics ? 
    Math.round((metrics.completedTasks / metrics.totalTasks) * 100) : 0;

  // Handle loading state
  if (isLoading || !data) {
    return (
      <CardContent className="h-full w-full flex flex-col items-center justify-center p-4" style={{ containerType: 'inline-size' }}>
        <h3 className="text-lg font-semibold mb-4">On-Time Performance</h3>
        <div className="w-full space-y-3">
          <Skeleton className="h-8 w-8 mx-auto" />
          <Skeleton className="h-4 w-3/4 mx-auto" />
          <Skeleton className="h-3 w-1/2 mx-auto" />
        </div>
      </CardContent>
    );
  }

  // Handle error state - but ignore if metrics are valid (partial success)
  const hasValidMetrics = metrics && 
    typeof metrics.totalTasks === 'number' && 
    typeof metrics.completedTasks === 'number' &&
    metrics.onTimeOrEarly && 
    metrics.late;
  
  if ((error || data.error) && !hasValidMetrics) {
    return (
      <CardContent className="h-full w-full flex flex-col items-center justify-center p-4" style={{ containerType: 'inline-size' }}>
        <h3 className="text-lg font-semibold mb-2">On-Time Performance</h3>
        <Alert variant="destructive" className="w-full max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex flex-col gap-2">
            <span>{error || data.errorMessage || 'Failed to load performance data'}</span>
            <Button
              variant="outline"
              size="sm"
              className="mt-2 self-start"
              onClick={() => refetch()}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Try Again
            </Button>
          </AlertDescription>
        </Alert>
      </CardContent>
    );
  }

  return (
    <CardContent className="h-full w-full flex flex-col p-4" style={{ containerType: 'inline-size' }}>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">On-Time Performance</h3>
        {/* <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => refetch()}
          title="Refresh data"
        >
          <RefreshCw className="h-4 w-4" />
        </Button> */}
      </div>
      
      <div className="flex flex-col items-center justify-center gap-2 w-full h-full">
        <span className={`scalable-text ${status.color}`} style={{ fontSize: '2.5rem' }}>
          {status.icon}
        </span>
        <span className={`scalable-text-small font-medium text-center ${status.color}`}>
          {status.text}
        </span>
        
        {metrics && (
          <div className="w-full mt-3 text-center">
            <p className="text-sm text-muted-foreground">
              {metrics.completedTasks} of {metrics.totalTasks} tasks completed
            </p>
            <div className="w-full bg-gray-200 rounded-full h-2.5 mt-1">
              <div 
                className="bg-blue-600 h-2.5 rounded-full" 
                style={{ width: `${completionRate}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {completionRate}% completion rate
            </p>
          </div>
        )}
      </div>
      
      <div className="mt-3 text-xs text-muted-foreground text-center flex items-center justify-center gap-1">
        {data.lastUpdated && (
          <span>Updated: {(() => {
            const cleanedDate = cleanDateAndTimeObject(data.lastUpdated);
            return cleanedDate ? cleanedDate.toLocaleTimeString() : 'Invalid date';
          })()}</span>
        )}
        {data.error && hasValidMetrics && (
          <button 
            className="text-orange-400 hover:text-orange-300 cursor-pointer" 
            title="Click for details"
            onClick={() => setShowErrorDialog(true)}
          >
            ⚡
          </button>
        )}
      </div>

      {/* Error details dialog */}
      <Dialog open={showErrorDialog} onOpenChange={setShowErrorDialog}>
        <DialogContent className="max-w-md bg-card">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-orange-400">⚡</span>
              Partial Data Warning
            </DialogTitle>
            <DialogDescription>
              The data collector encountered an issue but was still able to retrieve metrics.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-3">
            <div className="rounded-md bg-muted p-3">
              <p className="text-sm font-mono text-muted-foreground break-all">
                {data.errorMessage || 'Unknown error'}
              </p>
            </div>
            <p className="text-xs text-muted-foreground">
              This is typically a minor issue that doesn't affect the displayed data. 
              If you continue to see this, please contact support.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </CardContent>
  );
};

export default OnTimePerformanceWidget;
